import { useState } from 'react'
import { Plus } from 'lucide-react'
import { ApiError } from '@/api/errors'
import { useWbProgramMutations } from '@/api/queries'
import { StatusBadge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Combobox, type ComboboxOption } from '@/components/ui/Combobox'
import { Field, Input } from '@/components/ui/Field'
import { ConfirmDialog, Drawer } from '@/components/ui/Overlay'
import { useToast } from '@/components/ui/Toast'
import type { Program, Segment, SlateProgram, WbProgram } from '@/types'
import { SegmentField } from './ProgramFields'
import { ProgramContentEditor } from './ProgramContentEditor'

/**
 * One WB program (PC-33, ADR-0019): its name, segment and slate program, and
 * the crawled programs beneath it, one per degree. Adding one ("Add program",
 * a quick add) asks for the name and segment only: a WB program needs no
 * crawled program.
 *
 * Its slate program is the one it writes (PC-37). Which WB program each slate
 * program maps to is set on the Slate tab; the two may disagree (ADR-0021).
 *
 * Name, segment and slate program save together; the content (PC-9) saves on
 * its own. A degree opens the program drawer.
 */
export function WbProgramDrawer({
  clientId,
  wbProgram,
  initialName,
  segments,
  slatePrograms,
  programs,
  onClose,
  onCreated,
  onOpenProgram,
  onAddDegree,
}: {
  clientId: string
  wbProgram: WbProgram | 'new' | null
  /** Fills in the name when adding: a quick add (PC-31). */
  initialName?: string
  segments: Segment[]
  slatePrograms: SlateProgram[]
  /** The client's crawled programs; the drawer lists this WB program's survivors. */
  programs: Program[]
  onClose: () => void
  onCreated?: (wb: WbProgram) => void
  onOpenProgram: (program: Program) => void
  onAddDegree: (wb: WbProgram) => void
}) {
  if (!wbProgram) return null
  // Keyed, so opening another WB program starts from its own values.
  return (
    <WbProgramForm
      key={wbProgram === 'new' ? `new:${initialName ?? ''}` : wbProgram.id}
      clientId={clientId}
      wbProgram={wbProgram === 'new' ? null : wbProgram}
      initialName={initialName}
      segments={segments}
      slatePrograms={slatePrograms}
      programs={programs}
      onClose={onClose}
      onCreated={onCreated}
      onOpenProgram={onOpenProgram}
      onAddDegree={onAddDegree}
    />
  )
}

function WbProgramForm({
  clientId,
  wbProgram,
  initialName,
  segments,
  slatePrograms,
  programs,
  onClose,
  onCreated,
  onOpenProgram,
  onAddDegree,
}: {
  clientId: string
  wbProgram: WbProgram | null
  initialName?: string
  segments: Segment[]
  slatePrograms: SlateProgram[]
  programs: Program[]
  onClose: () => void
  onCreated?: (wb: WbProgram) => void
  onOpenProgram: (program: Program) => void
  onAddDegree: (wb: WbProgram) => void
}) {
  const ops = useWbProgramMutations(clientId)
  const toast = useToast()
  const [name, setName] = useState(wbProgram?.name ?? initialName ?? '')
  const [segmentId, setSegmentId] = useState<string | null>(wbProgram?.segmentId ?? null)
  const [slateProgramId, setSlateProgramId] = useState<string | null>(wbProgram?.slateProgram?.id ?? null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const degrees = wbProgram ? programs.filter((p) => p.wbProgramId === wbProgram.id && !p.mergedInto) : []
  const savedSlate = wbProgram?.slateProgram?.id ?? null
  const changed =
    !wbProgram || name.trim() !== wbProgram.name || segmentId !== wbProgram.segmentId || slateProgramId !== savedSlate
  const busy = ops.create.isPending || ops.update.isPending

  // Enabled slate programs, and the current one even if disabled. One that maps to another WB program says which.
  const slateOptions: ComboboxOption[] = slatePrograms
    .filter((s) => s.enabled || s.id === savedSlate)
    .map((s) => ({
      value: s.id,
      label: s.enabled ? s.slateName : `${s.slateName} (disabled)`,
      hint: s.wbProgram && s.wbProgram.id !== wbProgram?.id ? `Maps to ${s.wbProgram.name}` : undefined,
    }))

  async function save() {
    const trimmed = name.trim()
    try {
      if (wbProgram) {
        const patch = {
          ...(trimmed !== wbProgram.name ? { name: trimmed } : {}),
          ...(segmentId !== wbProgram.segmentId ? { segmentId } : {}),
          ...(slateProgramId !== savedSlate ? { slateProgramId } : {}),
        }
        await ops.update.mutateAsync({ id: wbProgram.id, patch })
        toast.push('Program saved.')
      } else {
        const created = await ops.create.mutateAsync({ name: trimmed, segmentId })
        toast.push(`Added ${created.name}.`)
        onCreated?.(created)
      }
    } catch (e) {
      // The drawer stays open with what was typed.
      const taken = e instanceof ApiError && e.status === 409
      toast.push(taken ? `A program named ${trimmed} is already in the list.` : 'Could not save the program.', 'error')
      return
    }
    onClose()
  }

  return (
    <>
      <Drawer
        open
        onClose={onClose}
        title={wbProgram ? wbProgram.name : 'Add program'}
        subtitle={
          wbProgram && (
            <span className="text-ink-faint">
              {degrees.length === 0 ? 'Added by hand, no crawled programs' : `${degrees.length} crawled ${degrees.length === 1 ? 'program' : 'programs'}`}
            </span>
          )
        }
        footer={
          <>
            {wbProgram && (
              <Button variant="ghost" onClick={() => setConfirmDelete(true)}>
                Delete
              </Button>
            )}
            <span className="flex-1" />
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="primary" onClick={() => void save()} disabled={!name.trim() || !changed} loading={busy}>
              {wbProgram ? 'Save changes' : 'Add program'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field
            label="WB program"
            required
            hint="The name the team publishes: the dropdown value in the translations table. No degree: the degrees are listed below."
          >
            {(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} />}
          </Field>

          <SegmentField value={segmentId} segments={segments} onChange={setSegmentId} />

          {wbProgram && (
            <Field
              label="Slate program"
              hint="The slate prompt the translations table writes for this program. Which WB program each slate program maps to is set on the Slate tab."
            >
              {(p) => (
                <Combobox
                  {...p}
                  value={slateProgramId}
                  options={slateOptions}
                  onChange={setSlateProgramId}
                  noneLabel="None"
                  searchLabel="Search slate programs"
                />
              )}
            </Field>
          )}

          {wbProgram && (
            <section className="space-y-2 border-t border-rule pt-4">
              <h3 className="field-label">Content</h3>
              <p className="text-[12px] text-ink-faint">
                Saved on its own. Slate programs mapped here show this content unless they have their own.
              </p>
              <ProgramContentEditor clientId={clientId} owner="wb_program" ownerId={wbProgram.id} />
            </section>
          )}

          {wbProgram && (
            <section className="space-y-2 border-t border-rule pt-4">
              <div className="flex items-center">
                <h3 className="field-label flex-1">Degrees</h3>
                <Button size="sm" onClick={() => onAddDegree(wbProgram)}>
                  <Plus size={13} aria-hidden />
                  Add degree
                </Button>
              </div>
              {degrees.length === 0 ? (
                <p className="text-[12px] text-ink-faint">No crawled programs. Assign them from the Unassigned tab.</p>
              ) : (
                <ul className="divide-y divide-rule rounded-[6px] border border-rule">
                  {degrees.map((p) => (
                    <li key={p.id}>
                      <button
                        type="button"
                        onClick={() => onOpenProgram(p)}
                        className="flex w-full items-center gap-3 px-3 py-2 text-left text-[13px] hover:bg-surface-sunk"
                      >
                        <span className="min-w-0 flex-1 truncate">
                          {p.name}
                          <span className="text-ink-faint"> · {p.degree ?? 'No degree'}</span>
                        </span>
                        <StatusBadge status={p.status} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}
        </div>
      </Drawer>

      {wbProgram && (
        <ConfirmDialog
          open={confirmDelete}
          onClose={() => setConfirmDelete(false)}
          title={`Delete ${wbProgram.name}?`}
          description={
            degrees.length > 0
              ? `Its ${degrees.length} crawled ${degrees.length === 1 ? 'program moves' : 'programs move'} to Unassigned, and its slate programs are unmapped.`
              : 'Its slate programs are unmapped.'
          }
          confirmLabel="Delete program"
          destructive
          busy={ops.remove.isPending}
          onConfirm={async () => {
            try {
              await ops.remove.mutateAsync(wbProgram.id)
            } catch {
              toast.push('Could not delete the program.', 'error')
              return
            }
            setConfirmDelete(false)
            toast.push(`Deleted ${wbProgram.name}.`)
            onClose()
          }}
        />
      )}
    </>
  )
}
